import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Shield, Lock, Check, X, KeyRound, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { projectRolesAPI, adminAPI } from '../../utils/api';
import { useToast, apiError } from '../../components/Toast';

const LEVEL_META = {
  SystemAdmin: { color:'#2E539F', label:'System Administrator' },
  Admin:       { color:'#2E539F', label:'Admin (Limited)'      },
  Executive:   { color:'#8b5cf6', label:'Executive'            },
  Manager:     { color:'#0891b2', label:'Manager'              },
  Specialist:  { color:'#10b981', label:'Specialist'           },
  Developer:   { color:'#6d28d9', label:'Developer'            },
};

const LEVEL_ORDER = ['SystemAdmin','Admin','Executive','Manager','Specialist','Developer'];
const VALID_LEVELS = LEVEL_ORDER;

const PERMISSION_MATRIX = [
  { section:'User App', rows:[
    { label:'View Dashboard',          levels:['SystemAdmin','Admin','Executive','Manager','Specialist','Developer'] },
    { label:'View All Projects',       levels:['SystemAdmin','Admin','Executive','Manager'] },
    { label:'View Assigned Projects',  levels:['Specialist','Developer'] },
    { label:'Create Projects',         levels:['SystemAdmin','Admin','Manager'] },
    { label:'Delete Projects',         levels:['SystemAdmin','Admin'] },
    { label:'Create Tasks',            levels:['SystemAdmin','Admin','Manager','Specialist','Developer'] },
    { label:'Delete Any Task',         levels:['SystemAdmin','Admin','Manager'] },
    { label:'Manage Milestones',       levels:['SystemAdmin','Admin','Manager'] },
    { label:'Approve Milestones',      levels:['SystemAdmin','Admin','Manager'] },
    { label:'Manage Risk Register',    levels:['SystemAdmin','Admin','Manager'] },
    { label:'View Reports',            levels:['SystemAdmin','Admin','Executive','Manager'] },
    { label:'Manage Users Directory',  levels:['SystemAdmin','Admin','Manager'] },
  ]},
  { section:'Dev Hub', rows:[
    { label:'Create Bug Reports',      levels:['SystemAdmin','Admin','Manager','Specialist','Developer'] },
    { label:'Delete Bug Reports',      levels:['SystemAdmin','Admin','Manager'] },
    { label:'Create Change Requests',  levels:['SystemAdmin','Admin','Manager','Specialist','Developer'] },
    { label:'Approve Change Requests', levels:['SystemAdmin','Admin','Manager'] },
  ]},
  { section:'Admin Panel', rows:[
    { label:'Access Admin Panel',      levels:['SystemAdmin','Admin'] },
    { label:'User Management',         levels:['SystemAdmin','Admin'] },
    { label:'Roles & Permissions',     levels:['SystemAdmin','Admin'] },
    { label:'Page Access Control',     levels:['SystemAdmin','Admin'] },
    { label:'Masters / Picklists',     levels:['SystemAdmin','Admin'] },
    { label:'Analytics',               levels:['SystemAdmin','Admin'] },
    { label:'System Settings',         levels:['SystemAdmin'] },
    { label:'Data Management',         levels:['SystemAdmin'] },
    { label:'Audit Logs',              levels:['SystemAdmin'] },
  ]},
];

export default function AdminRoleManagementPage() {
  const toast = useToast();
  const [roles,      setRoles]      = useState([]);
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [tab,        setTab]        = useState('roles');
  const [search,     setSearch]     = useState('');
  const [levelFilter,setLevelFilter]= useState('All');
  const [sortCol,    setSortCol]    = useState('name');
  const [sortDir,    setSortDir]    = useState('asc');

  // Add modal
  const [showAdd,   setShowAdd]   = useState(false);
  const [addForm,   setAddForm]   = useState({ name:'', perm_level:'Specialist', description:'' });
  const [addError,  setAddError]  = useState('');
  const [addSaving, setAddSaving] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [rr, ur] = await Promise.all([
        projectRolesAPI.getAllRoles(),
        adminAPI.getUsers({}),
      ]);
      setRoles(rr.data || []);
      setUsers(ur.data || []);
    } catch(e) { setError(e.response?.data?.message || 'Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) return setAddError('Role name is required');
    setAddSaving(true); setAddError('');
    try {
      await projectRolesAPI.createRole(addForm);
      setShowAdd(false);
      setAddForm({ name:'', perm_level:'Specialist', description:'' });
      load();
    } catch(e) { setAddError(e.response?.data?.message || 'Failed to create role'); }
    finally { setAddSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await projectRolesAPI.deleteRole(deleteTarget.id);
      setDeleteTarget(null); load();
    } catch(e) { toast.error(apiError(e, 'Failed to delete role')); }
    finally { setDeleting(false); }
  };

  // Filtered + sorted roles
  const filtered = roles
    .filter(r => levelFilter === 'All' || r.perm_level === levelFilter)
    .filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()) || (r.description||'').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let av = a[sortCol] ?? ''; let bv = b[sortCol] ?? '';
      if (sortCol === 'user_count') { av = parseInt(av)||0; bv = parseInt(bv)||0; }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const SortIcon = ({ col }) => sortCol !== col ? null :
    sortDir === 'asc' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>;

  const Th = ({ col, children, center }) => (
    <th onClick={() => handleSort(col)}
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider cursor-pointer select-none hover:bg-[var(--surface)] transition-colors ${center?'text-center':''}`}
      style={{ color:'var(--text-secondary)' }}>
      <span className="flex items-center gap-1 justify-start">{children}<SortIcon col={col}/></span>
    </th>
  );

  const usersByRole = users.reduce((acc, u) => { acc[u.role]=(acc[u.role]||0)+1; return acc; }, {});

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Role Management</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
            Manage system roles, assign permission levels, and view the access matrix.
          </p>
        </div>
        <button onClick={() => { setAddForm({ name:'', perm_level:'Specialist', description:'' }); setAddError(''); setShowAdd(true); }}
          className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={14}/> Add Role
        </button>
      </div>

      {error && <div className="p-3 rounded-xl text-sm" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444' }}>⚠ {error}</div>}

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor:'var(--border)' }}>
        {[['roles','Role Directory'],['matrix','Permission Matrix']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
            className="px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px"
            style={{ borderColor:tab===k?'#2E539F':'transparent', color:tab===k?'#2E539F':'var(--text-secondary)' }}>
            {l}
          </button>
        ))}
      </div>

      {/* ── TAB 1: ROLE DIRECTORY ─────────────────────────────────────────── */}
      {tab === 'roles' && (
        <div className="card overflow-hidden p-0">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b"
            style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}/>
              <input className="input pl-8 text-sm" placeholder="Search roles..."
                value={search} onChange={e => setSearch(e.target.value)}/>
            </div>
            <select className="select text-sm" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
              <option value="All">All Levels</option>
              {LEVEL_ORDER.map(l => <option key={l} value={l}>{LEVEL_META[l]?.label || l}</option>)}
            </select>
            <span className="text-xs" style={{ color:'var(--text-secondary)' }}>
              {filtered.length} of {roles.length} roles
            </span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-3xl mb-2">🔍</p>
              <p className="font-semibold" style={{ color:'var(--text)' }}>No roles match</p>
              <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>Try adjusting your search or filter</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background:'var(--surface-alt)', borderBottom:'2px solid var(--border)' }}>
                    <Th col="name">Role Name</Th>
                    <Th col="perm_level">Permission Level</Th>
                    <Th col="description">Description</Th>
                    <Th col="user_count">Users</Th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-center"
                      style={{ color:'var(--text-secondary)' }}>Type</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider"
                      style={{ color:'var(--text-secondary)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((role, i) => {
                    const meta  = LEVEL_META[role.perm_level] || LEVEL_META.Specialist;
                    const count = parseInt(role.user_count) || 0;
                    const roleUsers = users.filter(u => u.role === role.name).slice(0, 4);
                    return (
                      <tr key={role.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors"
                        style={{ borderColor:'var(--border)', background:i%2===1?'rgba(0,0,0,.015)':'transparent' }}>

                        {/* Role Name */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ background:meta.color }}>
                              {role.name[0].toUpperCase()}
                            </div>
                            <span className="font-medium" style={{ color:'var(--text)' }}>{role.name}</span>
                          </div>
                        </td>

                        {/* Level */}
                        <td className="px-4 py-3">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                            style={{ background:`${meta.color}18`, color:meta.color }}>
                            {meta.label}
                          </span>
                        </td>

                        {/* Description */}
                        <td className="px-4 py-3 max-w-xs">
                          <span className="text-sm truncate block" style={{ color:'var(--text-secondary)' }}>
                            {role.description || '—'}
                          </span>
                        </td>

                        {/* Users */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {roleUsers.map(u => (
                                <div key={u.id}
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-[var(--surface)] -ml-1 first:ml-0"
                                  style={{ background:u.avatar_color||meta.color }} title={u.name}>
                                  {u.name?.split(' ').map(n=>n[0]).join('').slice(0,2)}
                                </div>
                              ))}
                            </div>
                            <Link to={`/admin/users?role=${encodeURIComponent(role.name)}`}
                              className="text-xs hover:underline" style={{ color:count>0?meta.color:'var(--text-secondary)' }}>
                              {count} user{count!==1?'s':''}
                            </Link>
                          </div>
                        </td>

                        {/* Type badge */}
                        <td className="px-4 py-3 text-center">
                          {role.is_system
                            ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded"
                                style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
                                <Lock size={10}/> System
                              </span>
                            : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded"
                                style={{ background:'rgba(92,196,188,.1)', color:'#5CC3BC' }}>
                                ✦ Custom
                              </span>
                          }
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Link to="/admin/roles" title="Project permissions"
                              className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
                              style={{ color:'#2E539F' }}>
                              <Shield size={13}/>
                            </Link>
                            <Link to="/admin/page-access" title="Page access"
                              className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
                              style={{ color:'#5CC3BC' }}>
                              <KeyRound size={13}/>
                            </Link>
                            {!role.is_system && (
                              <button onClick={() => setDeleteTarget(role)} title="Delete role"
                                className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition-colors">
                                <Trash2 size={13}/>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Table footer */}
          {!loading && (
            <div className="px-4 py-2.5 border-t text-xs" style={{ borderColor:'var(--border)', color:'var(--text-secondary)', background:'var(--surface-alt)' }}>
              {filtered.length} role{filtered.length!==1?'s':''} · {roles.filter(r=>r.is_system).length} system · {roles.filter(r=>!r.is_system).length} custom
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: PERMISSION MATRIX ────────────────────────────────────── */}
      {tab === 'matrix' && (
        <div className="card overflow-hidden p-0">
          <div className="px-5 py-3 border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
            <p className="font-semibold text-sm" style={{ color:'var(--text)' }}>System Permission Matrix</p>
            <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>
              Reference grid showing what each permission level can do. Configure per-role behaviour in
              <Link to="/admin/roles" style={{ color:'#2E539F' }}> Project Permissions</Link> and
              <Link to="/admin/page-access" style={{ color:'#2E539F' }}> Page Access</Link>.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background:'var(--surface-alt)', borderBottom:'2px solid var(--border)' }}>
                  <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider"
                    style={{ color:'var(--text-secondary)', minWidth:220 }}>Permission</th>
                  {LEVEL_ORDER.map(level => {
                    const m = LEVEL_META[level];
                    return (
                      <th key={level} className="px-3 py-3 text-center text-xs font-bold"
                        style={{ color:m.color, minWidth:95 }}>
                        {m.label.split(' ')[0]}
                        <div className="text-xs font-normal" style={{ color:'var(--text-secondary)' }}>
                          {roles.filter(r=>r.perm_level===level).length} role{roles.filter(r=>r.perm_level===level).length!==1?'s':''}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_MATRIX.map(({ section, rows }) => (
                  <React.Fragment key={section}>
                    <tr style={{ background:'rgba(46,83,159,.05)', borderTop:'2px solid var(--border)' }}>
                      <td colSpan={LEVEL_ORDER.length+1} className="px-5 py-2">
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color:'#2E539F' }}>{section}</span>
                      </td>
                    </tr>
                    {rows.map((row, ri) => (
                      <tr key={row.label} className="border-b hover:bg-[var(--surface-alt)] transition-colors"
                        style={{ borderColor:'var(--border)', background:ri%2===1?'rgba(0,0,0,.012)':'transparent' }}>
                        <td className="px-5 py-2.5 text-sm" style={{ color:'var(--text)' }}>{row.label}</td>
                        {LEVEL_ORDER.map(level => (
                          <td key={level} className="px-3 py-2.5 text-center">
                            {row.levels.includes(level)
                              ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ background:'rgba(16,185,129,.12)' }}><Check size={12} style={{ color:'#10b981' }}/></span>
                              : <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ background:'rgba(148,163,184,.1)' }}><X size={12} style={{ color:'#94a3b8' }}/></span>
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-2.5 border-t flex items-center gap-4 text-xs" style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
            <span className="flex items-center gap-1"><Check size={11} style={{ color:'#10b981' }}/> Allowed</span>
            <span className="flex items-center gap-1"><X size={11} style={{ color:'#94a3b8' }}/> Denied</span>
          </div>
        </div>
      )}

      {/* ── ADD ROLE MODAL ──────────────────────────────────────────────── */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowAdd(false)}/>
          <div className="relative w-full max-w-md rounded-2xl shadow-2xl z-10 p-6"
            style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <h3 className="font-bold text-lg mb-4" style={{ color:'var(--text)' }}>Add New Role</h3>
            {addError && <div className="p-3 rounded-xl text-sm mb-4" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444' }}>{addError}</div>}
            <div className="space-y-4">
              <div>
                <label className="label">Role Name <span className="text-red-500">*</span></label>
                <input className="input" value={addForm.name} autoFocus placeholder="e.g. Senior Business Analyst"
                  onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}/>
              </div>
              <div>
                <label className="label">Permission Level <span className="text-red-500">*</span></label>
                <select className="select" value={addForm.perm_level}
                  onChange={e => setAddForm(f => ({ ...f, perm_level: e.target.value }))}>
                  {VALID_LEVELS.map(l => <option key={l} value={l}>{LEVEL_META[l].label} — {l}</option>)}
                </select>
                <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                  Determines what this role can access across the system.
                </p>
              </div>
              <div>
                <label className="label">Description</label>
                <input className="input" value={addForm.description} placeholder="Describe this role's responsibilities"
                  onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))}/>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6 pt-4 border-t" style={{ borderColor:'var(--border)' }}>
              <button onClick={() => setShowAdd(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleAdd} disabled={addSaving} className="btn-primary flex items-center gap-2">
                {addSaving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                Create Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRMATION ──────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteTarget(null)}/>
          <div className="relative w-full max-w-sm rounded-2xl shadow-2xl z-10 p-6"
            style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <h3 className="font-bold mb-2" style={{ color:'var(--text)' }}>Delete Role</h3>
            <p className="text-sm mb-3" style={{ color:'var(--text-secondary)' }}>
              Delete <strong>"{deleteTarget.name}"</strong>?
            </p>
            {(parseInt(deleteTarget.user_count)||0) > 0 && (
              <div className="p-3 rounded-xl text-sm mb-3" style={{ background:'rgba(245,158,11,.08)', color:'#92400e', border:'1px solid rgba(245,158,11,.2)' }}>
                ⚠ {deleteTarget.user_count} user{parseInt(deleteTarget.user_count)!==1?'s are':' is'} assigned this role and will need to be reassigned.
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="px-4 py-2 rounded-xl text-sm font-medium text-white flex items-center gap-2"
                style={{ background:'#ef4444' }}>
                {deleting && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
